// PHA-567: a single row inside the AI Insights band. Compact: tier dot,
// patient name, why-now line, headline sparkline. Click → opens AiInsightDrawer.

import React from 'react';
import { Badge, Box, Flex, Text } from '@radix-ui/themes';
import type { PatientAiInsight } from '../types';
import TrendSparkline from './TrendSparkline';

interface AiInsightCardProps {
  insight: PatientAiInsight;
  onSelect: (insight: PatientAiInsight) => void;
}

const tierGlyph = (tier: PatientAiInsight['risk_tier']): string => {
  if (tier === 'critical') return '🔴';
  if (tier === 'watch') return '🟠';
  return '🟢';
};

export const AiInsightCard: React.FC<AiInsightCardProps> = ({ insight, onSelect }) => {
  // The first trend callout is the "headline" sparkline shown inline.
  const headline = insight.trend_callouts[0];
  const sparkline = headline ? insight.sparkline_series[headline.sparkline_series_id] || [] : [];
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onSelect(insight)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(insight);
        }
      }}
      style={{
        cursor: 'pointer',
        padding: '8px 10px',
        borderBottom: '1px solid var(--gray-4)',
        outline: 'none',
      }}
      className="hover:bg-slate-50 focus:bg-slate-100"
    >
      <Flex align="center" gap="2">
        <Text size="2">{tierGlyph(insight.risk_tier)}</Text>
        {insight.live_signal && (
          <Badge color="red" variant="soft" size="1" title="Live reading after last AI computation">
            ● LIVE
          </Badge>
        )}
        <Text size="2" weight="bold" style={{ minWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {insight.patient_name || insight.patient_id}
        </Text>
        <Text size="2" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {insight.why_now}
        </Text>
        {sparkline.length > 1 && (
          <TrendSparkline points={sparkline} width={70} height={20} ariaLabel="trend" />
        )}
      </Flex>
    </Box>
  );
};

export default AiInsightCard;
